<template>
    <section class="uses-tailwind">
        <h2
            @click="$router.push('/workstation/' + entry.id)"
            class="cursor-pointer hover:underline text-center text-lg font-bold"
        >
            {{ entry.name }}
        </h2>
        <production-progress-bar
            :progress="entry.progress"
            class="mt-2"
        />
        <h3 class="text-center text-base mt-2">
            {{
                trans('production_progress.products_count', {
                    count: remainingProductsCount,
                })
            }}
        </h3>
        <table
            class="table-auto text-sm w-full mt-2 p-2"
            v-if="entry.products.length > 0"
        >
            <tbody>
                <tr
                    v-for="product in entry.products"
                    :key="product.id"
                    :class="getRowClass(product.workdaysRemaining, product)"
                    :title="
                        trans('production_progress.workdays', {
                            count: product.workdaysRemaining,
                        })
                    "
                >
                    <td
                        class="align-text-top hover:underline cursor-pointer"
                        @click="goToProductPage(product.id)"
                    >
                        {{ product.number }}
                    </td>
                    <td class="align-text-top">
                        {{ product.customer.name || '/' }}
                    </td>
                    <td class="align-text-top">
                        {{ product.productType.name }}
                    </td>
                    <td class="align-text-top">
                        {{ product.status }}
                    </td>
                </tr>
            </tbody>
        </table>
    </section>
</template>

<script>
// Requires API to return lastStatusChangeDate for each product
import ProductionProgressBar from './ProductionProgressBar.vue';
import NavigationFunctionsMixin from '../../mixins/navigation-functions.js';
import settingsApi from '../../api/settings';
import { parseTimeString, calculateWorkHoursElapsed } from '../../helpers/WorkHoursHelper';

export default {
    props: {
        entry: {
            type: Object,
            required: true,
        },
        holidays: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            hoursLimit: null,
            workWindows: null,
        };
    },
    computed: {
        remainingProductsCount() {
            return Math.max(
                parseInt(this.entry.productsCount) - parseInt(this.entry.finishedProductsCount),
                0
            );
        },
        hasSettings() {
            return this.hoursLimit !== null && this.workWindows !== null;
        },
    },
    methods: {
        loadSettings() {
            const DEFAULTS = {
                hoursLimit: 4,
                workWindows: [
                    { start: 7.5, end: 12 },
                    { start: 13, end: 16.5 },
                ],
            };

            const keys = [
                'scheduleProductionStatusDelayHours',
                'scheduleWorkHoursStart1',
                'scheduleWorkHoursEnd1',
                'scheduleWorkHoursStart2',
                'scheduleWorkHoursEnd2',
            ];

            settingsApi
                .fetchSettings(keys)
                .then((response) => {
                    const data = response.data.data;
                    if (data) {
                        const parsed = parseInt(data.scheduleProductionStatusDelayHours);
                        this.hoursLimit = !isNaN(parsed) && parsed > 0 ? parsed : DEFAULTS.hoursLimit;

                        this.workWindows = [
                            {
                                start: data.scheduleWorkHoursStart1 ? parseTimeString(data.scheduleWorkHoursStart1) : DEFAULTS.workWindows[0].start,
                                end: data.scheduleWorkHoursEnd1 ? parseTimeString(data.scheduleWorkHoursEnd1) : DEFAULTS.workWindows[0].end,
                            },
                            {
                                start: data.scheduleWorkHoursStart2 ? parseTimeString(data.scheduleWorkHoursStart2) : DEFAULTS.workWindows[1].start,
                                end: data.scheduleWorkHoursEnd2 ? parseTimeString(data.scheduleWorkHoursEnd2) : DEFAULTS.workWindows[1].end,
                            },
                        ];
                    }
                })
                .catch((error) => {
                    console.warn('Failed to load work hours settings:', error);
                });
        },
        getWorkHoursElapsed(product) {
            if (!this.hasSettings) {
                return 0;
            }

            if (!product?.lastStatusChangeDate) {
                console.warn(`Product ${product?.id} missing lastStatusChangeDate for work hours calculation`);
                return 0;
            }

            return calculateWorkHoursElapsed(product.lastStatusChangeDate, this.workWindows, this.holidays);
        },
        getRowClass(workdaysRemaining, product) {
            const classes = [];

            if (workdaysRemaining <= 0) {
                classes.push('bg-red-800', 'text-white');
            } else if (workdaysRemaining <= 2) {
                classes.push('bg-red-200');
            } else if (workdaysRemaining <= 4) {
                classes.push('bg-amber-200');
            }

            if (this.hasSettings && this.getWorkHoursElapsed(product) >= this.hoursLimit) {
                classes.push('font-bold');
            }

            return classes.join(' ');
        },
    },
    mounted() {
        this.loadSettings();
    },
    components: {
        ProductionProgressBar,
    },
    mixins: [NavigationFunctionsMixin],
};
</script>